import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

const STORE_TOKEN = "hasfarm_store_token";
const STORE_OBJ = "hasfarm_store_obj";
const ADMIN_TOKEN = "hasfarm_admin_token";

export const tokens = {
  store: () => localStorage.getItem(STORE_TOKEN),
  admin: () => localStorage.getItem(ADMIN_TOKEN),
  setStore: (t, s) => { localStorage.setItem(STORE_TOKEN, t); localStorage.setItem(STORE_OBJ, JSON.stringify(s)); },
  setAdmin: (t) => localStorage.setItem(ADMIN_TOKEN, t),
  storeObj: () => { try { return JSON.parse(localStorage.getItem(STORE_OBJ)); } catch { return null; } },
  clearStore: () => { localStorage.removeItem(STORE_TOKEN); localStorage.removeItem(STORE_OBJ); },
  clearAdmin: () => localStorage.removeItem(ADMIN_TOKEN),
};

const storeAxios = axios.create({ baseURL: API });
storeAxios.interceptors.request.use((c) => {
  const t = tokens.store();
  if (t) c.headers.Authorization = `Bearer ${t}`;
  return c;
});

const adminAxios = axios.create({ baseURL: API });
adminAxios.interceptors.request.use((c) => {
  const t = tokens.admin();
  if (t) c.headers.Authorization = `Bearer ${t}`;
  return c;
});

export function formatApiErrorDetail(detail) {
  if (detail == null) return "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) return detail.map((e) => (e && e.msg ? e.msg : JSON.stringify(e))).join(" ");
  if (detail && detail.msg) return detail.msg;
  return String(detail);
}

export const CUSTOMER_ID = "metcash";

export const api = {
  loginStore: async (store_code, pin, customer_id = CUSTOMER_ID) => {
    const { data } = await storeAxios.post("/auth/login", { store_code, pin, customer_id });
    tokens.setStore(data.access_token, data.store);
    return data;
  },
  me: () => storeAxios.get("/auth/me").then((r) => r.data),
  catalogue: () => storeAxios.get("/catalogue").then((r) => r.data),
  specials: () => storeAxios.get("/specials").then((r) => r.data),
  schedule: () => storeAxios.get("/order-schedule").then((r) => r.data),
  deliveryOptions: () => storeAxios.get("/delivery-options").then((r) => r.data),
  orders: (scope = "all") => storeAxios.get(`/orders?scope=${scope}`).then((r) => r.data),
  createOrder: (body) => storeAxios.post("/orders", body).then((r) => r.data),
  reorder: (id) => storeAxios.post(`/orders/${id}/reorder`).then((r) => r.data),
  toggleStanding: (id) => storeAxios.post(`/orders/${id}/toggle-standing`).then((r) => r.data),
  decks: () => storeAxios.get("/home/decks").then((r) => r.data),
  events: () => storeAxios.get("/events").then((r) => r.data),
  eventFlowers: (key) => storeAxios.get(`/events/${key}/flowers`).then((r) => r.data),
  changePin: (old_pin, new_pin) => storeAxios.post("/auth/change-pin", { old_pin, new_pin }).then((r) => r.data),
  forgotPin: (store_code, contact) => storeAxios.post("/auth/forgot-pin", { store_code, contact, customer_id: CUSTOMER_ID }).then((r) => r.data),
};

export const adminApi = {
  login: async (password, email) => {
    const { data } = await adminAxios.post("/admin/login", { password, email: email || null });
    tokens.setAdmin(data.access_token);
    return data;
  },
  me: () => adminAxios.get("/admin/me").then((r) => r.data),
  listUsers: () => adminAxios.get("/admin/users").then((r) => r.data),
  createUser: (body) => adminAxios.post("/admin/users", body).then((r) => r.data),
  updateUser: (id, body) => adminAxios.patch(`/admin/users/${id}`, body).then((r) => r.data),
  resendInvite: (id) => adminAxios.post(`/admin/users/${id}/resend-invite`).then((r) => r.data),
  deleteUser: (id) => adminAxios.delete(`/admin/users/${id}`).then((r) => r.data),
  activityLog: (q = "", limit = 300) => adminAxios.get(`/admin/users/activity?limit=${limit}&q=${encodeURIComponent(q)}`).then((r) => r.data),
  changePassword: (current_password, new_password) => adminAxios.post("/admin/change-password", { current_password, new_password }).then((r) => r.data),
  setRolePerm: (role, area, level) => adminAxios.put(`/admin/users/roles/${role}`, { area, level }).then((r) => r.data),
  resetRolePerms: (role) => adminAxios.delete(`/admin/users/roles/${role}`).then((r) => r.data),
  inviteInfo: (token) => axios.get(`${API}/admin/invite/${token}`).then((r) => r.data),
  acceptInvite: async (token, password) => {
    const { data } = await axios.post(`${API}/admin/accept-invite`, { token, password });
    tokens.setAdmin(data.access_token);
    return data;
  },
  forgotPassword: (email) => axios.post(`${API}/admin/forgot-password`, { email }).then((r) => r.data),
  stores: (customer_id = CUSTOMER_ID) => adminAxios.get(`/admin/stores?customer_id=${customer_id}`).then((r) => r.data),
  createStore: (body) => adminAxios.post("/admin/stores", { ...body, customer_id: CUSTOMER_ID }).then((r) => r.data),
  patchStore: (code, body) => adminAxios.patch(`/admin/stores/${code}`, body).then((r) => r.data),
  deleteStore: (code) => adminAxios.delete(`/admin/stores/${code}`).then((r) => r.data),
  renameStore: (code, new_code) => adminAxios.post(`/admin/stores/${code}/rename`, { new_code }).then((r) => r.data),
  allOrders: (scope = "all", customer_id = CUSTOMER_ID) =>
    adminAxios.get(`/admin/orders?scope=${scope}&customer_id=${customer_id}`).then((r) => r.data),
  updateOrderStatus: (id, status) => adminAxios.patch(`/admin/orders/${id}`, { status }).then((r) => r.data),
  flowers: () => adminAxios.get("/admin/flowers").then((r) => r.data),
  setFlowerImage: (id, image_url) => adminAxios.post(`/admin/flowers/${id}/image`, { image_url }).then((r) => r.data),
  bulkFlowerImages: (items) => adminAxios.post("/admin/flowers/bulk-images", { items }).then((r) => r.data),
  bulkStores: (stores) => adminAxios.post("/admin/stores/bulk", { stores, customer_id: CUSTOMER_ID }).then((r) => r.data),
  bulkUpdateStores: (store_codes, patch) => adminAxios.post("/admin/stores/bulk-update", { store_codes, ...patch }).then((r) => r.data),
  bulkDeleteStores: (store_codes) => adminAxios.post("/admin/stores/bulk-delete", { store_codes }).then((r) => r.data),
  productionReport: (week_commencing, delivery_day, into_store_date, event_key) => {
    const p = new URLSearchParams({ customer_id: CUSTOMER_ID });
    if (week_commencing) p.set("week_commencing", week_commencing);
    if (delivery_day) p.set("delivery_day", delivery_day);
    if (into_store_date) p.set("into_store_date", into_store_date);
    if (event_key) p.set("event_key", event_key);
    return adminAxios.get(`/admin/production-report?${p.toString()}`).then((r) => r.data);
  },
  productionPdfUrl: (week_commencing, delivery_day, into_store_date, ext = "pdf", event_key = "") => {
    const p = new URLSearchParams({ customer_id: CUSTOMER_ID, token: tokens.admin() || "" });
    if (week_commencing) p.set("week_commencing", week_commencing);
    if (delivery_day) p.set("delivery_day", delivery_day);
    if (into_store_date) p.set("into_store_date", into_store_date);
    if (event_key) p.set("event_key", event_key);
    return `${API}/admin/production-report.${ext}?${p.toString()}`;
  },
  listEvents: () => adminAxios.get("/admin/events").then((r) => r.data),
  upsertEvent: (body) => adminAxios.post("/admin/events", body).then((r) => r.data),
  deleteEvent: (id) => adminAxios.delete(`/admin/events/${id}`).then((r) => r.data),
  eventFlowers: (key) => adminAxios.get(`/admin/events/${key}/flowers`).then((r) => r.data),
  eventBulkFlowers: (key, flowers) => adminAxios.post(`/admin/events/${key}/flowers/bulk`, { flowers }).then((r) => r.data),
  eventCutoffReminders: (key, dry_run = true) => adminAxios.post(`/admin/events/${key}/cutoff-reminders?dry_run=${dry_run}`).then((r) => r.data),
  eventImportFromDeck: (key) => adminAxios.post(`/admin/events/${key}/import-from-deck`).then((r) => r.data),
  eventBulkImages: (key, items) => adminAxios.post(`/admin/events/${key}/flowers/bulk-images`, { items }).then((r) => r.data),
  emailProduction: (week_commencing, delivery_day, into_store_date) =>
    adminAxios.post("/admin/production-report/email", { week_commencing, delivery_day, into_store_date, customer_id: CUSTOMER_ID }).then((r) => r.data),
  storeOrdersReport: (week_commencing, delivery_day, into_store_date) => {
    const p = new URLSearchParams({ customer_id: CUSTOMER_ID });
    if (week_commencing) p.set("week_commencing", week_commencing);
    if (delivery_day) p.set("delivery_day", delivery_day);
    if (into_store_date) p.set("into_store_date", into_store_date);
    return adminAxios.get(`/admin/store-orders-report?${p.toString()}`).then((r) => r.data);
  },
  storeOrdersUrl: (week_commencing, delivery_day, into_store_date, ext = "pdf") => {
    const p = new URLSearchParams({ customer_id: CUSTOMER_ID, token: tokens.admin() || "" });
    if (week_commencing) p.set("week_commencing", week_commencing);
    if (delivery_day) p.set("delivery_day", delivery_day);
    if (into_store_date) p.set("into_store_date", into_store_date);
    return `${API}/admin/store-orders-report.${ext}?${p.toString()}`;
  },
  emailStoreOrders: (week_commencing, delivery_day, into_store_date) =>
    adminAxios.post("/admin/store-orders-report/email", { week_commencing, delivery_day, into_store_date, customer_id: CUSTOMER_ID }).then((r) => r.data),
  storeOrdersLastEmailed: (into_store_date, week_commencing) =>
    adminAxios.get(`/admin/store-orders-report/last-emailed?into_store_date=${into_store_date || ""}&week_commencing=${week_commencing || ""}`).then((r) => r.data),
  storeReportEdits: (into_store_date) => adminAxios.get(`/admin/store-orders-report/edits?into_store_date=${into_store_date}&customer_id=${CUSTOMER_ID}`).then((r) => r.data),
  saveStoreReportEdit: (body) => adminAxios.put("/admin/store-orders-report/edits", { ...body, customer_id: CUSTOMER_ID }).then((r) => r.data),
  restoreStoreReportEdit: (id) => adminAxios.post(`/admin/store-orders-report/edits/restore/${id}`).then((r) => r.data),
  resetStoreReportEdits: (into_store_date) => adminAxios.delete(`/admin/store-orders-report/edits?into_store_date=${into_store_date}&customer_id=${CUSTOMER_ID}`).then((r) => r.data),
  savePoNumber: (week_commencing, delivery_day, po_number) =>
    adminAxios.post("/admin/production-report/po-number", { week_commencing, delivery_day, po_number, customer_id: CUSTOMER_ID }).then((r) => r.data),
  sapStatus: () => adminAxios.get("/admin/sap/status").then((r) => r.data),
  sapPreview: (into_store_date) => adminAxios.post("/admin/sap/preview", { into_store_date, customer_id: CUSTOMER_ID }).then((r) => r.data),
  sapSend: (into_store_date, confirm_resend = false) => adminAxios.post("/admin/sap/send", { into_store_date, confirm_resend, customer_id: CUSTOMER_ID }).then((r) => r.data),
  sapHistory: (limit = 20) => adminAxios.get(`/admin/sap/history?customer_id=${CUSTOMER_ID}&limit=${limit}`).then((r) => r.data),
  reportEdits: (into_store_date, event_key = "") => adminAxios.get(`/admin/production-report/edits?into_store_date=${into_store_date}&customer_id=${CUSTOMER_ID}&event_key=${event_key}`).then((r) => r.data),
  saveReportEdit: (body) => adminAxios.put("/admin/production-report/edits", { ...body, customer_id: CUSTOMER_ID }).then((r) => r.data),
  restoreReportEdit: (id) => adminAxios.post(`/admin/production-report/edits/restore/${id}`).then((r) => r.data),
  resetReportEdits: (into_store_date, event_key = "") => adminAxios.delete(`/admin/production-report/edits?into_store_date=${into_store_date}&customer_id=${CUSTOMER_ID}&event_key=${event_key}`).then((r) => r.data),
  importSapCodes: (rows) => adminAxios.post("/admin/flowers/sap-codes", { rows }).then((r) => r.data),
  sapRangePreview: () => adminAxios.get("/admin/sap/range/preview").then((r) => r.data),
  sapRangeApply: (body) => adminAxios.post("/admin/sap/range/apply", body).then((r) => r.data),
  importContactEmails: (rows) => adminAxios.post("/admin/stores/contact-emails", { rows }).then((r) => r.data),
  generatePin: (code) => adminAxios.post(`/admin/stores/${code}/generate-pin`).then((r) => r.data),
  bulkGeneratePins: (body) => adminAxios.post("/admin/stores/bulk-generate-pin", body).then((r) => r.data),
  restoreFlowers: (flower_ids) => adminAxios.post("/admin/flowers/restore", { flower_ids }).then((r) => r.data),
  restoreStores: (store_codes) => adminAxios.post("/admin/stores/restore", { store_codes }).then((r) => r.data),
  getSchedule: () => adminAxios.get(`/admin/schedule?customer_id=${CUSTOMER_ID}`).then((r) => r.data),
  saveSchedule: (body) => adminAxios.put("/admin/schedule", { ...body, customer_id: CUSTOMER_ID }).then((r) => r.data),
  getLanding: () => adminAxios.get("/landing").then((r) => r.data),
  saveLanding: (body) => adminAxios.put("/admin/landing", body).then((r) => r.data),
  getAppearance: () => adminAxios.get("/appearance").then((r) => r.data),
  saveAppearance: (body) => adminAxios.put("/admin/appearance", body).then((r) => r.data),
  getColumnOrder: () => adminAxios.get("/admin/production-report/column-order").then((r) => r.data),
  saveColumnOrder: (flower_ids) => adminAxios.put("/admin/production-report/column-order", { flower_ids }).then((r) => r.data),
  eventReminderStatus: (key) => adminAxios.get(`/admin/events/${key}/reminder-status`).then((r) => r.data),
  listDecks: () => adminAxios.get("/admin/home/decks").then((r) => r.data),
  updateDeck: (key, body) => adminAxios.put(`/admin/home/decks/${key}`, body).then((r) => r.data),
  reorderDecks: (keys) => adminAxios.post("/admin/home/decks/reorder", { keys }).then((r) => r.data),
  missingOrders: (week_commencing, delivery_day) => {
    const p = new URLSearchParams({ customer_id: CUSTOMER_ID });
    if (week_commencing) p.set("week_commencing", week_commencing);
    if (delivery_day) p.set("delivery_day", delivery_day);
    return adminAxios.get(`/admin/missing-orders?${p.toString()}`).then((r) => r.data);
  },
  sendReminders: (body) => adminAxios.post("/admin/send-reminders", { ...body, customer_id: CUSTOMER_ID }).then((r) => r.data),
  reminderLog: (days = 14) => adminAxios.get(`/admin/reminder-log?customer_id=${CUSTOMER_ID}&days=${days}`).then((r) => r.data),
  standingSettings: () => adminAxios.get("/admin/standing-orders/settings").then((r) => r.data),
  saveStandingSettings: (body) => adminAxios.put("/admin/standing-orders/settings", body).then((r) => r.data),
  standingTemplates: () => adminAxios.get(`/admin/standing-orders/templates?customer_id=${CUSTOMER_ID}`).then((r) => r.data),
  standingPreview: () => adminAxios.get(`/admin/standing-orders/preview?customer_id=${CUSTOMER_ID}`).then((r) => r.data),
  standingRunNow: () => adminAxios.post(`/admin/standing-orders/run-now?customer_id=${CUSTOMER_ID}`).then((r) => r.data),
  standingHistory: (limit = 10) => adminAxios.get(`/admin/standing-orders/history?customer_id=${CUSTOMER_ID}&limit=${limit}`).then((r) => r.data),
  listSpecials: () => adminAxios.get("/admin/specials").then((r) => r.data),
  upsertSpecial: (body) => adminAxios.post("/admin/specials", body).then((r) => r.data),
  deleteSpecial: (id) => adminAxios.delete(`/admin/specials/${id}`).then((r) => r.data),
  supportCounts: () => adminAxios.get(`/admin/support/counts?customer_id=${CUSTOMER_ID}`).then((r) => r.data),
  getReminderSchedule: () => adminAxios.get("/admin/reminder-schedule").then((r) => r.data),
  saveReminderSchedule: (body) => adminAxios.put("/admin/reminder-schedule", body).then((r) => r.data),
  runRemindersNow: () => adminAxios.post("/admin/reminder-schedule/run-now").then((r) => r.data),
  upsertFlower: (body) => adminAxios.post("/admin/flowers", body).then((r) => r.data),
  deleteFlower: (id) => adminAxios.delete(`/admin/flowers/${id}`).then((r) => r.data),
  setFlowersVisibility: (flower_ids, hidden) => adminAxios.post("/admin/flowers/visibility", { flower_ids, hidden }).then((r) => r.data),
  bulkDeleteFlowers: (flower_ids) => adminAxios.post("/admin/flowers/bulk-delete", { flower_ids }).then((r) => r.data),
  listStoreContacts: (code) => adminAxios.get(`/admin/stores/${code}/contacts`).then((r) => r.data),
  addStoreContact: (code, body) => adminAxios.post(`/admin/stores/${code}/contacts`, body).then((r) => r.data),
  updateStoreContact: (code, id, body) => adminAxios.patch(`/admin/stores/${code}/contacts/${id}`, body).then((r) => r.data),
  deleteStoreContact: (code, id) => adminAxios.delete(`/admin/stores/${code}/contacts/${id}`).then((r) => r.data),
  pendingReviews: () => adminAxios.get(`/admin/orders/pending-reviews?customer_id=${CUSTOMER_ID}`).then((r) => r.data),
};

export function mondayOf(d = new Date()) {
  const x = new Date(d);
  const day = (x.getDay() + 6) % 7; // Mon=0
  x.setDate(x.getDate() - day);
  return x.toISOString().slice(0, 10);
}
